<script lang="ts" setup>
import { useWallet } from '@solana/wallet-adapter-vue'
import { Screen } from 'quasar'
import InputWidget from '~/components/Widgets/InputWidget.vue'
import { JSTAKING_VALIDATOR_VOTE } from '~/config'
import { lamportsToSol } from '~/utils'

const MIN_STAKE_AMOUNT = 0.003
const { publicKey } = useWallet()
const deposit = useDeposit()
const balanceStore = useBalanceStore()

const connectDialog = ref(false)

const nativeBalance = computed(() => lamportsToSol(balanceStore.nativeBalance ?? 0))

const inputLabelinfo = computed(() => {
  return publicKey.value
    ? `${nativeBalance.value} SOL available`
    : 'Wallet not connected'
})

const solToStake = ref()

const btnSizeByScreen = computed(() => Screen.xs ? 'md' : 'lg')

const disabled = computed(() => {
  return !publicKey.value
    || !solToStake.value
    || Number(solToStake.value) > Number(nativeBalance.value)
    || solToStake.value === '.'
    || Number(solToStake.value) <= 0
    || Number(solToStake.value) < MIN_STAKE_AMOUNT
})

async function stakeHandler() {
  if (!publicKey.value) {
    connectDialog.value = true
    return
  }
  if (disabled.value) {
    return
  }
  if (Number(solToStake.value) > 0) {
    await deposit.createStake(
      Number(solToStake.value),
      JSTAKING_VALIDATOR_VOTE,
    )
  }
}
</script>

<template>
  <div class="stake-card">
    <div class="stake__input">
      <input-widget
        v-model="solToStake"
        size="lg"
        label="You are staking"
        :label-info="inputLabelinfo"
        :rules="publicKey ? [
          (value) => !value || Number(value) <= Number(nativeBalance) || 'Insufficient funds',
          (value) => !value || Number(value) >= MIN_STAKE_AMOUNT || `Min. ${MIN_STAKE_AMOUNT} SOL`,
        ] : []"
      />
    </div>
    <div class="stake__params square-block">
      <div class="params-left">
        <estimate-calc :amount="solToStake" />
      </div>
    </div>

    <div class="stake__action square-block">
      <stake-fee fee-amount="0.0008" />
      <div class="stake__action-wrapper">
        <app-btn
          :loading="deposit.loading.value"
          :disable="disabled && !!publicKey"
          :size="btnSizeByScreen"
          variant="filled"
          class="stake__action-btn"
          @click="stakeHandler"
        >
          Stake SOL
        </app-btn>
      </div>
    </div>
  </div>

  <connect-from-stake-dialog v-model="connectDialog" @close-dialog="connectDialog = false" />
</template>

<style lang="scss">
.stake-card {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  flex-direction: column;
  height: 100%;

  .stake {
    &__input {
      width: 100%;
      padding-bottom: 24px;

      @media (max-width: $breakpoint-xs) {
        padding-bottom: 16px;
      }
    }

    &__params {
      width: 100%;
      display: grid;
      //grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      gap: 4px;
      padding: 4px;
      margin-bottom: 32px;

      @media (max-width: $breakpoint-xs) {
        display: flex;
        flex-direction: column;
        margin-bottom: 8px;
      }

      .params-left {
        height: 100%;

        .gray-button {
          height: 100%;
        }
      }

      .params-right {
        height: 100%;
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 4px;

        @media (max-width: $breakpoint-xs) {
          flex-direction: row;

          .gray-button {
            flex: 1;
            height: 82px;
          }
        }

        .gray-button {
          min-height: 48px;
        }

        &__item {
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;

          @media (max-width: $breakpoint-xs) {
            flex-direction: column;
            align-items: flex-start;

            span:first-child {
              font-size: 12px;
              line-height: 16px;
            }

            span:last-child {
              font-size: 14px;
              color: $zinc-50;
              font-weight: 500;
            }
          }

          span {
            line-height: 100%;
          }
        }
      }
    }

    &__action {
      width: 100%;
      margin-top: auto;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 28px;

      @media (max-width: $breakpoint-xs) {
        margin-top: 20px;
      }

      .q-btn {
        width: 180px;

        .q-btn__content {
          font-size: 16px !important;
        }
      }

      &-wrapper {
        width: max-content;
        text-align: right;
      }
    }
  }
}
</style>
